// Shared session-failure vocabulary for the token getters and SessionRecovery.

/** Fired when the session is known-dead: a terminal refresh failure or
 *  expired grant. SessionRecovery listens and round-trips through Know Identity Shell. */
export const SESSION_EXPIRED_EVENT = 'knowsh:unauthorized';

export function signalSessionExpired(): void {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

/** oidc-client-ts error codes and conditions that mean the grant itself is dead —
 *  no retry will ever succeed and the only fix is an interactive round-trip.
 *  Everything else (timeouts, network blips) is transient and must NOT be treated as a logout. */
const TERMINAL_ERROR_CODES = new Set([
  'invalid_grant',
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'expired_token',
]);

export function isTerminalAuthError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) {
    return false;
  }

  const candidate = err as {
    error?: unknown;
    message?: unknown;
    innerError?: unknown;
  };

  const code =
    typeof candidate.error === 'string'
      ? candidate.error
      : typeof (candidate.innerError as { error?: unknown } | undefined)?.error === 'string'
        ? ((candidate.innerError as { error?: unknown }).error as string)
        : undefined;

  if (code && TERMINAL_ERROR_CODES.has(code)) {
    return true;
  }

  const message =
    typeof candidate.message === 'string'
      ? candidate.message
      : typeof (candidate.innerError as { message?: unknown } | undefined)?.message === 'string'
        ? ((candidate.innerError as { message?: unknown }).message as string)
        : '';

  const lowerMsg = message.toLowerCase();
  if (
    lowerMsg.includes('invalid_grant') ||
    lowerMsg.includes('login_required') ||
    lowerMsg.includes('refresh_token is required') ||
    lowerMsg.includes('missing refresh token') ||
    lowerMsg.includes('expired refresh') ||
    lowerMsg.includes('refresh token expired') ||
    lowerMsg.includes('refresh token is expired')
  ) {
    return true;
  }

  return false;
}
